import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Maximize2 } from 'lucide-react';

interface CarouselItem {
  id: number;
  image: string;
  keyword: string;
  description: string;
}

interface DiagonalCarouselProps {
  items: CarouselItem[];
  autoPlay?: boolean;
  interval?: number;
}

const DiagonalCarousel: React.FC<DiagonalCarouselProps> = ({ 
  items, 
  autoPlay = true, 
  interval = 4000 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  useEffect(() => {
    if (!autoPlay) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, interval);

    return () => clearInterval(timer);
  }, [autoPlay, interval, items.length]);

  const getItemPosition = (index: number) => {
    const diff = index - currentIndex;
    if (diff === 0) return 'current';
    if (diff === 1 || diff === -(items.length - 1)) return 'next';
    if (diff === -1 || diff === items.length - 1) return 'prev';
    return 'hidden';
  };

  const getPositionStyles = (position: string) => {
    switch (position) {
      case 'current':
        return {
          x: 0,
          y: 0,
          scale: 1,
          opacity: 1,
          zIndex: 3,
        };
      case 'next':
        return {
          x: 120,
          y: -60,
          scale: 0.85,
          opacity: 0.7,
          zIndex: 2,
        };
      case 'prev':
        return {
          x: -120,
          y: 60,
          scale: 0.85,
          opacity: 0.7,
          zIndex: 2,
        };
      default:
        return {
          x: 0,
          y: 0,
          scale: 0.6,
          opacity: 0,
          zIndex: 1,
        };
    }
  };

  return (
    <div className="relative w-full h-96 md:h-[400px] lg:h-[450px] xl:h-[500px] overflow-hidden">
      <AnimatePresence>
        {items.map((item, index) => {
          const position = getItemPosition(index);
          const styles = getPositionStyles(position);

          return (
            <motion.div
              key={item.id}
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={styles}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.8,
                ease: "easeInOut",
              }}
              style={{
                filter: position === 'current' ? 'none' : 'blur(1px)',
                display: position === 'hidden' ? 'none' : 'flex',
              }}
              onClick={() => {
                if (position === 'prev') {
                  setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
                } else if (position === 'next') {
                  setCurrentIndex((prev) => (prev + 1) % items.length);
                }
              }}
            >
              <div className={`relative bg-gradient-card rounded-2xl shadow-card border border-white/50 backdrop-blur-sm max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl w-full mx-4 overflow-hidden group transition-all duration-300 ${
                position === 'prev' || position === 'next' 
                  ? 'cursor-pointer hover:shadow-lg hover:scale-105 hover:border-white/70' 
                  : ''
              }`}>
                <div className="relative w-full h-80 md:h-[270px] lg:h-[315px] xl:h-[360px] bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 flex items-center justify-center overflow-hidden group">
                  {item.image && (
                    <img 
                      src={item.image} 
                      alt={item.keyword}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFullscreenImage(item.image);
                    }}
                    className="absolute top-2 right-2 p-2 bg-black/20 hover:bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 backdrop-blur-sm z-20"
                    aria-label="View fullscreen"
                  >
                    <Maximize2 size={16} className="text-white" />
                  </button>
                </div>
                {position === 'current' && (
                  <>
                    <div 
                      className="absolute left-0 top-0 w-1/2 h-full z-10 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
                      }}
                    />
                    <div 
                      className="absolute right-0 top-0 w-1/2 h-full z-10 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentIndex((prev) => (prev + 1) % items.length);
                      }}
                    />
                  </>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-secondary/80 via-secondary/40 to-transparent p-2 transform translate-y-2 transition-transform duration-300">
                  {/* <motion.h3 
                    className="text-2xl md:text-3xl font-bold text-white mb-2"
                    animate={{
                      scale: position === 'current' ? [1, 1.02, 1] : 1,
                    }}
                    transition={{
                      duration: 3,
                      repeat: position === 'current' ? Infinity : 0,
                      repeatType: "reverse",
                    }}
                  >
                    {item.keyword}
                  </motion.h3> */}
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentIndex
                ? 'bg-gradient-to-r from-primary to-accent scale-125'
                : 'bg-white/50 hover:bg-white/80'
            }`}
          />
        ))}
      </div>

      {fullscreenImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setFullscreenImage(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <img 
              src={fullscreenImage} 
              alt="Fullscreen view"
              className="max-w-full max-h-full object-contain"
            />
            <button
              onClick={() => setFullscreenImage(null)}
              className="absolute top-4 right-4 p-2 bg-black/40 hover:bg-black/60 rounded-lg backdrop-blur-sm"
              aria-label="Close fullscreen"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiagonalCarousel;